import { useEffect, useRef, useState } from 'react';

interface IScrollVideoProps {
  className?: string;
  videoFPS: number;
  animationBoxOffsetTop?: number;
  animationBoxOffsetBottom?: number;
  videoSource: string;
}

const ScrollVideo = ({
  className = '',
  animationBoxOffsetTop = 0,
  animationBoxOffsetBottom = 0,
  videoFPS,
  videoSource,
}: IScrollVideoProps) => {
  const bodyHeight = document.body.getBoundingClientRect().height;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      document.removeEventListener('scroll', onPageScroll, true);
    };
  }, []);

  const onPageScroll = () => {
    if (!videoRef?.current) {
      return;
    }
    const videoHeight = videoRef.current!.getBoundingClientRect().height;
    const videoScrollOffset = videoRef.current.getBoundingClientRect().y;
    const animationOffsetTop = videoRef.current.height * animationBoxOffsetTop;
    const animationOffsetBottom = videoRef.current.height * animationBoxOffsetBottom;
    const videoStep = 1 / videoFPS;
    const videoSteps = videoRef.current.duration / videoStep;
    const scrollAnimationTrack = bodyHeight + videoHeight - animationOffsetTop;
    const scrollStep = scrollAnimationTrack / videoSteps;

    if (
      videoScrollOffset + animationOffsetTop < bodyHeight &&
      videoScrollOffset + videoHeight - animationOffsetBottom > 0
    ) {
      const offset = Math.round((bodyHeight - videoScrollOffset - animationOffsetTop) / scrollStep);

      if (offset * videoStep === 0 || offset * videoStep >= videoRef.current.duration) {
        return;
      }

      updateFrame(+(offset * videoStep).toFixed(2));
    }
  };

  const updateFrame = (offset: number) => {
    if (!videoRef?.current || videoRef.current.currentTime === offset) {
      return;
    }

    requestAnimationFrame(() => {
      if (videoRef.current!.currentTime !== offset) {
        videoRef.current!.currentTime = offset;
      }
    });
  };

  const onVideoLoaded = () => {
    if (isInitialized) {
      return;
    }

    document.removeEventListener('scroll', onPageScroll, true);
    document.addEventListener('scroll', onPageScroll, true);
    setIsInitialized(true);
  };

  return (
    <video
      muted
      playsInline
      controls={false}
      preload="metadata"
      className={className}
      ref={videoRef}
      onLoadedData={onVideoLoaded}
      onLoadedMetadata={onVideoLoaded}
    >
      <source src={videoSource} type="video/mp4" />
    </video>
  );
};

export default ScrollVideo;
